/**
 * `analytics-overview` / `analytics-dashboard` — the two read-only boards under
 * the **Analytics** nav section · CLM-81 (D-81-1…D-81-16, adopted 13 Sep 2026
 * per recommendation; Gerry may overturn; R-81-1 binds the two boards).
 *
 * Nothing here writes: both descriptors say `writes_audit_row: false` (the
 * `pipeline-status` D-70-2 precedent), no DDL, no new action — the gate is
 * `contract:read` (D-81-4). Every figure is one scoped `SELECT` anchored on
 * `root_contract_key` (rules 9, 10); `party_key` narrows beside the scope
 * predicate, never instead of it — out of scope ⇒ zeros, not 403 (D-81-5).
 *
 * ## No money figures (D-81-2)
 *
 * Contract value exists only as the extracted phrase `key_terms ->> 'contract_value'`
 * (0005:279-284) — there is no numeric column to sum — so no tile, table or
 * formatter here carries an amount. Add one when a parsed value column lands.
 *
 * ## Time to verify (D-81-6)
 *
 * `created_utc` → the first 0046 trigger row whose `new_value ->> 'data_trust'
 * = 'Verified'`, not `processing_status = 'Verified'` (a restricted contract
 * never reaches that status). Rows with no such row are `unbucketed` (D-81-13).
 */
import { z } from 'zod';
import { ContractKeySchema, IsoDateSchema, IsoTimestampUtcSchema, PartyKeySchema } from '../primitives.js';
import { REGIONS, RegionSchema } from '../domain/region.js';
import { CloudeqEntityKeySchema } from '../domain/cloudeq-entity.js';
import { ContractSummarySchema } from '../domain/contract.js';
import { CONTRACT_TYPES, ContractTypeConfirmationSchema, ContractTypeSchema, DataTrustSchema, LifecycleStatusSchema, NOTIFICATION_TIERS, NotificationTierSchema, PROCESSING_STATUSES, PartyTypeSchema, ProcessingStatusSchema, VerificationStatusSchema, } from '../vocabulary.js';
import { PageInfoSchema, PageRequestSchema } from '../pagination.js';
export const ANALYTICS_PERIODS = ['1w', '1m', '3m', '1y'];
export const AnalyticsPeriodSchema = z.enum(ANALYTICS_PERIODS);
/** Histogram buckets of the time-to-verify chart, in order (D-81-6). */
export const VERIFY_BUCKETS = ['lt_1d', 'd1_3', 'd3_7', 'w1_3', 'gt_3w'];
const Count = z.number().int().min(0);
const TileSchema = z.object({ n: Count, n_today: Count }).strict();
/** Fixed-key object; `V` is kept generic so `z.infer` yields `Record<key, number>`, not `Record<key, any>`. */
const keyed = (keys, value) => z.object(Object.fromEntries(keys.map((k) => [k, value]))).strict();
export const AnalyticsOverviewRequestSchema = z
    .object({
    /** Filters every tile by `created_utc`; default `1m` (D-81-14). */
    period: AnalyticsPeriodSchema.optional(),
    /** The contract's PRIMARY party. Out of scope ⇒ zeros, not 403 (D-81-5). */
    party_key: PartyKeySchema.optional(),
    /** CLM-87 — the PRIMARY party's region. AND-ed beside party_key and the scope (D-87-11). */
    region: RegionSchema.optional(),
    /** CLM-91 — the signing cloudEQ entity; equality on contract.cloudeq_entity_key beside the scope (D-91-5). */
    cloudeq_entity: CloudeqEntityKeySchema.optional(),
    /** The browser's local midnight as UTC — the "N today" boundary (D-81-9). Server default: `date_trunc('day', now())`. */
    day_start_utc: IsoTimestampUtcSchema.optional(),
})
    .strict();
export const AnalyticsClientSchema = z
    .object({ party_key: PartyKeySchema, display_name: z.string().min(1), party_type: PartyTypeSchema })
    .strict();
export const AnalyticsOverviewResponseSchema = z
    .object({
    period: AnalyticsPeriodSchema,
    party_key: PartyKeySchema.nullable(),
    region: RegionSchema.nullable(),
    cloudeq_entity: CloudeqEntityKeySchema.nullable(),
    day_start_utc: IsoTimestampUtcSchema,
    by_status: keyed(PROCESSING_STATUSES, TileSchema),
    /**
     * CLM-118 (B19, R-118-12) — `by_type.OTHER` excludes Type pending contracts: a
     * placeholder `OTHER` is never counted as `OTHER`.
     */
    by_type: keyed(CONTRACT_TYPES, TileSchema),
    /**
     * CLM-110 (R-110-23) — of each `by_type[t].n`, how many are still provisional
     * (no Admin has confirmed the type, and the file name and the AI have not
     * agreed). Same filters and scope as `by_type`, so it is never more than that
     * tile's `n`. Counts `provisional` only: neither Type pending nor
     * `auto_confirmed`.
     */
    by_type_provisional: keyed(CONTRACT_TYPES, Count),
    /** CLM-118 (B19, R-118-12) — contracts whose type is **Type pending**, its own tile. */
    by_type_pending: TileSchema,
    total_in_scope: Count,
    total_today: Count,
    /** Scoped option list for the Client selector — never a second endpoint (D-81-8). */
    clients: z.array(AnalyticsClientSchema),
    generated_utc: IsoTimestampUtcSchema,
})
    .strict();
export const AnalyticsDashboardRequestSchema = z
    .object({
    party_key: PartyKeySchema.optional(),
    /** CLM-87 — the PRIMARY party's region. AND-ed beside party_key and the scope (D-87-11). */
    region: RegionSchema.optional(),
    /** CLM-91 — the signing cloudEQ entity; equality on contract.cloudeq_entity_key beside the scope (D-91-5). */
    cloudeq_entity: CloudeqEntityKeySchema.optional(),
})
    .strict();
/**
 * `verified` counts `data_trust = 'Verified'` only — every field settled by a
 * person. CLM-108 adds `auto_approved` (`data_trust = 'AutoApproved'`, D-2.1-4)
 * beside it rather than folding it in, so a tile never silently loses a
 * contract nor reports one as human-verified that is not.
 */
export const AnalyticsByClientRowSchema = z
    .object({
    party_key: PartyKeySchema,
    display_name: z.string().min(1),
    contracts: Count,
    pending: Count,
    verified: Count,
    /** CLM-108 — contracts some of whose fields were settled by policy (`data_trust = 'AutoApproved'`). */
    auto_approved: Count,
    expiring_90: Count,
})
    .strict();
export const AnalyticsByReviewerRowSchema = z
    .object({
    actor_upn: z.string().min(1),
    verifications: Count,
    corrections: Count,
    rejections: Count,
    avg_hours_to_verify: Count.nullable(),
})
    .strict();
/**
 * CLM-87 — one row per region in the fixed order of `REGIONS`, then `null` = Unassigned
 * (a client nobody has placed). Always five rows, zeros included, so the web renders
 * five fixed tiles without a lookup (D-87-11).
 */
export const AnalyticsByRegionRowSchema = z
    .object({
    region: RegionSchema.nullable(),
    contracts: Count,
    pending: Count,
    verified: Count,
    /** CLM-108 — as on {@link AnalyticsByClientRowSchema}. */
    auto_approved: Count,
})
    .strict();
/** The fixed `by_region` order: the four regions, then Unassigned. */
export const BY_REGION_ORDER = [...REGIONS, null];
/**
 * CLM-91 — one row per ACTIVE registry entity in `sort_order`, then `null` = Unassigned
 * (no entity matched, or the scan was ambiguous). The spine is the registry, not a
 * constant, so the length is `active rows + 1` — never pinned — and `display_name`
 * travels with the row so the web renders tiles from the response (D-91-5).
 */
export const AnalyticsByCloudeqEntityRowSchema = z
    .object({
    entity_key: CloudeqEntityKeySchema.nullable(),
    display_name: z.string().nullable(),
    contracts: Count,
    pending: Count,
    verified: Count,
    /** CLM-108 — as on {@link AnalyticsByClientRowSchema}. */
    auto_approved: Count,
})
    .strict();
/** CLM-90 (D-90-2, D-90-10) — the due-for-renewal windows and the per-tier ledger status of the ≤ 90 d set. */
export const AnalyticsTierCountsSchema = z.object({ sent: Count, acknowledged: Count, not_sent: Count }).strict();
export const AnalyticsAttentionSchema = z
    .object({
    /** Cumulative from today, past-due included (D-90-3): due_30 ≤ due_60 ≤ due_90. */
    due_30: Count,
    due_60: Count,
    due_90: Count,
    /** due date < today, contract still Executed/Active. Subset of every window. */
    past_due: Count,
    by_tier: keyed(NOTIFICATION_TIERS, AnalyticsTierCountsSchema),
})
    .strict();
export const AnalyticsDashboardResponseSchema = z
    .object({
    party_key: PartyKeySchema.nullable(),
    region: RegionSchema.nullable(),
    cloudeq_entity: CloudeqEntityKeySchema.nullable(),
    kpis: z
        .object({
        active: Count,
        expiring_30: Count,
        expiring_60: Count,
        expiring_90: Count,
        overdue_obligations: Count,
        alerts_unacknowledged: Count,
        fields_pending: Count,
        /** CLM-85 — contracts converted with the tables_missing caveat. */
        tables_missing: Count,
    })
        .strict(),
    status_mix: z
        .object({
        counts: keyed(PROCESSING_STATUSES, Count),
        total: Count,
        /** Human trust only: the share with `data_trust = 'Verified'` (CLM-108 keeps it so). */
        verified_share: z.number().min(0).max(1).nullable(),
        /** CLM-108 — the share with `data_trust = 'AutoApproved'`; `null` exactly when `verified_share` is (no contracts). */
        auto_approved_share: z.number().min(0).max(1).nullable(),
    })
        .strict()
        .refine((mix) => (mix.verified_share === null) === (mix.auto_approved_share === null), {
        message: 'auto_approved_share is null exactly when verified_share is (an empty status mix)',
        path: ['auto_approved_share'],
    }),
    time_to_verify: z.object({ buckets: keyed(VERIFY_BUCKETS, Count), unbucketed: Count }).strict(),
    by_client: z.array(AnalyticsByClientRowSchema),
    /** CLM-87 — five rows always: North America, LATAM, APAC, EMEA, Unassigned (null). */
    by_region: z.array(AnalyticsByRegionRowSchema).length(BY_REGION_ORDER.length),
    /** CLM-91 — active registry rows in sort_order, then Unassigned (null) last; at least the Unassigned row. */
    by_cloudeq_entity: z.array(AnalyticsByCloudeqEntityRowSchema).min(1),
    by_reviewer: z.array(AnalyticsByReviewerRowSchema),
    generated_utc: IsoTimestampUtcSchema,
    /** CLM-90 — appended last (MERGE-ORDER): CLM-91 inserts by_cloudeq_entity after by_region. */
    attention: AnalyticsAttentionSchema,
})
    .strict();
/* --- CLM-90 · due for renewal (D-90-3, D-90-8, D-90-9, D-90-11) ----------- */
/**
 * Re-exported from `vocabulary.ts`, where CLM-108 minted `DATA_TRUST_STATUSES`
 * (five values, `AutoApproved` included); this module kept a local copy of
 * `ck_contract_data_trust` until then, so imports from here are unchanged.
 */
export { DataTrustSchema } from '../vocabulary.js';
export const RENEWAL_WINDOWS = ['30', '60', '90', 'all', 'past_due'];
export const RenewalWindowSchema = z.enum(RENEWAL_WINDOWS);
export const AnalyticsRenewalsDueRequestSchema = z
    .object({
    party_key: PartyKeySchema.optional(),
    region: RegionSchema.optional(),
    /** CLM-91 — the signing cloudEQ entity; equality on contract.cloudeq_entity_key beside the scope (D-91-5). */
    cloudeq_entity: CloudeqEntityKeySchema.optional(),
    /**
     * Omitted = '90'. Cumulative from today; past-due rows are in every
     * window (D-90-3). `'past_due'` (CLM-132) is the one non-cumulative
     * window — strictly `due_date < today` — so the Dashboard's "Past due"
     * tile can open the list filtered to what it counted, instead of `'all'`
     * (every in-force contract with a due date, past or future).
     */
    window: RenewalWindowSchema.optional(),
    page: PageRequestSchema.optional(),
})
    .strict();
/** D-90-10 — the ladder reads left to right: not_due → not_sent (window open, no ledger row) → sent → acknowledged. */
export const TIER_STATUSES = ['not_due', 'not_sent', 'sent', 'acknowledged'];
export const TierStatusSchema = z.enum(TIER_STATUSES);
export const AnalyticsTierStatusSchema = z
    .object({
    tier: NotificationTierSchema,
    status: TierStatusSchema,
    sent_utc: IsoTimestampUtcSchema.nullable(),
    acknowledged_by_upn: z.string().nullable(),
    acknowledged_utc: IsoTimestampUtcSchema.nullable(),
})
    .strict();
export const DUE_SOURCES = ['renewal_notice_date', 'expiry_date'];
export const DueSourceSchema = z.enum(DUE_SOURCES);
export const AnalyticsRenewalRowSchema = z
    .object({
    contract_key: ContractKeySchema,
    root_contract_key: ContractKeySchema,
    contract_type: ContractTypeSchema,
    /** CLM-110 (R-110-23) — rendered beside `contract_type` as its `provisional` tag. */
    contract_type_confirmation: ContractTypeConfirmationSchema,
    sow_number: z.string().nullable(),
    lifecycle_status: LifecycleStatusSchema,
    /** PRIMARY party; null on a row with no primary (an amendment's parties are its parent's). */
    client: AnalyticsClientSchema.nullable(),
    expiry_date: IsoDateSchema.nullable(),
    renewal_notice_date: IsoDateSchema.nullable(),
    /** COALESCE(renewal_notice_date, expiry_date) — the same occurrence vw_notification_due (0048) watches. */
    due_date: IsoDateSchema,
    due_source: DueSourceSchema,
    /** (due_date - current_date) in SQL; negative = past due (D-90-9). */
    days_left: z.number().int(),
    data_trust: DataTrustSchema,
    /** The field_verification row for the feeding field; null when the pipeline never queued one (manual entry). */
    due_date_verification: z
        .object({ status: VerificationStatusSchema, confirmed_value: z.string().nullable() })
        .strict()
        .nullable(),
    /** Five rows always, NOTIFICATION_TIERS order (D-90-10). */
    notifications: z.array(AnalyticsTierStatusSchema).length(NOTIFICATION_TIERS.length),
})
    .strict();
export const AnalyticsRenewalsDueResponseSchema = z
    .object({
    party_key: PartyKeySchema.nullable(),
    region: RegionSchema.nullable(),
    window: RenewalWindowSchema,
    items: z.array(AnalyticsRenewalRowSchema),
    page: PageInfoSchema,
    generated_utc: IsoTimestampUtcSchema,
})
    .strict();
/* --- CLM-95 · KPI / status-mix contract list (D-95-1) --------------------- */
/**
 * The list behind a KPI or status-mix tile — CLM-95, D-95-1. Not `search`:
 * `search` requires a text query (`q`, min length 1) baked unconditionally
 * into its SQL, so "everything in scope matching these filters" has no path
 * there. This is a strict subset of `SearchNarrowSchema` minus `q`, following
 * `analytics-renewals-due`'s shape (query transport, `contract:read`,
 * paginated, scoped on `root_contract_key`, read-only, no audit row).
 *
 * `expiring_within_days` computes its own **exclusive** upper bound
 * server-side (`expiry_date < current_date + days`), matching
 * `kpiContractsQuery`'s `expiringWithin()` exactly — the caller never supplies
 * a date range and so can never hit the inclusive/exclusive mismatch that a
 * raw `expiring_to` would.
 */
export const AnalyticsContractsListRequestSchema = z
    .object({
    party_key: PartyKeySchema.optional(),
    region: RegionSchema.optional(),
    cloudeq_entity: CloudeqEntityKeySchema.optional(),
    lifecycle_status: z.array(LifecycleStatusSchema).min(1).optional(),
    processing_status: z.array(ProcessingStatusSchema).min(1).optional(),
    expiring_within_days: z.number().int().min(1).max(3660).optional(),
    page: PageRequestSchema.optional(),
})
    .strict();
export const AnalyticsContractsListResponseSchema = z
    .object({
    party_key: PartyKeySchema.nullable(),
    region: RegionSchema.nullable(),
    cloudeq_entity: CloudeqEntityKeySchema.nullable(),
    items: z.array(ContractSummarySchema),
    page: PageInfoSchema,
    generated_utc: IsoTimestampUtcSchema,
})
    .strict();
//# sourceMappingURL=analytics.js.map